<template>
  <section>
    <v-card
      :color="colorPad"
      :dark="darkMode"
      flat
      class="mb-4"
      v-if="showAmount"
    >
      <v-card-text class="d-flex justify-content-between py-5 px-3">
        <v-btn icon @click="clear" color="red">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
        <div class="text-center">
          <div class="font-weight-bold text-h4">
            {{ formattedValue }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <div class="numeric-keyboard">
      <v-row v-for="(row, rowIndex) in keys" :key="rowIndex" class="ma-0">
        <v-col
          v-for="key in row"
          :key="key && key.id ? key.id : rowIndex + '-' + $index"
          :cols="key.cols || 3"
          class="pa-0"
        >
          <v-btn
            v-if="key && (key.label || key.icon)"
            tile
            :class="key.class || ''"
            @click="
              key.action
                ? key.action()
                : key.label
                ? appendToInput(key.label)
                : null
            "
          >
            <template
              v-if="
                currencyData &&
                key.id === 23 &&
                currencyData.input_buffer &&
                currencyData.input_buffer !== '0' &&
                currencyData.input_buffer !== '0.00' &&
                currencyData.code &&
                currencyData.code !== 'LAK'
              "
            >
              <div class="col">
                <v-avatar size="24">
                  <v-img
                    width="24"
                    height="24"
                    :src="currencyData.flag"
                  ></v-img>
                </v-avatar>
                <div class="text-body-2 primary--text font-weight-bold">
                  {{ currencyData.input_buffer | NumberFormat }}
                </div>
              </div>
            </template>
            <template v-else>
              <v-icon v-if="key.icon && key.id !== 23">{{ key.icon }}</v-icon>
              <v-img
                class="mr-2 white--text"
                v-else-if="key.icon && key.id === 23"
                :src="key.icon"
                width="35"
                height="35"
                contain
              />
              <span v-else-if="key.label">{{ key.label }}</span>
            </template>
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </section>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  name: "NumericKeyboard",
  mixins: [myMixin],
  props: {
    open_noted: {
      type: Boolean,
      default: false,
    },
    showNote: {
      type: Boolean,
      default: true,
    },
    totalPrice: {
      type: Number,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: "OTHER", // Changed from RESTAURANT to OTHER to show all buttons
    },
    currencyData: {
      type: Object,
      default: null,
    },
    showAmount: {
      type: Boolean,
      default: true,
    },
    debug: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      windowListener: this.handleKeydown,
    };
  },

  mounted() {
    try {
      this.windowListener = this.handleKeydown.bind(this);
      window.addEventListener("keydown", this.windowListener);
    } catch (error) {
      console.error("Error in mounted lifecycle:", error);
    }
  },

  watch: {
    open_noted(newVal, oldVal) {
      // Only re-add event listener if the value actually changed
      if (newVal !== oldVal) {
        // remove event listener and add it again
        window.removeEventListener("keydown", this.windowListener);
        this.windowListener = this.handleKeydown.bind(this);
        window.addEventListener("keydown", this.windowListener);
      }
    },
    totalPrice(newVal, oldVal) {
      // Watch for totalPrice changes if needed
    },
    value(newVal, oldVal) {
      if (this.debug && newVal !== oldVal) {
        this.dlog("value changed", { oldVal, newVal });
      }
    },
    currencyData: {
      handler(n, o) {
        if (this.debug) {
          this.dlog("currencyData changed", { old: o, next: n });
        }
      },
      deep: true,
    },
  },

  destroyed() {
    try {
      if (this.windowListener) {
        window.removeEventListener("keydown", this.windowListener);
      }
    } catch (error) {
      console.error("Error in destroyed lifecycle:", error);
    }
  },

  computed: {
    colorPad() {
      try {
        return localStorage.getItem("darkMode") === "true" ? "#333" : "#f0f0f0";
      } catch (error) {
        console.error("Error getting color pad:", error);
        return "#f0f0f0";
      }
    },
    darkMode() {
      try {
        return localStorage.getItem("darkMode") === "true";
      } catch (error) {
        return false;
      }
    },
    formattedValue() {
      try {
        // Use the same NumberFormat filter as the header component
        const formatted = this.$options.filters.NumberFormat(this.value);
        if (this.debug) {
          const containsLetters = /[A-Za-z]/.test(String(formatted));
          this.dlog("formattedValue composed", {
            raw: this.value,
            formatted,
            containsLetters,
            currency: this.currencyData?.code,
            rate: this.currencyData?.store_exchange_rate,
          });
        }
        return formatted;
      } catch (error) {
        console.error("Error formatting value:", error);
        return this.value ? this.value.toString() : "0.00";
      }
    },
    keys() {
      try {
        const result = this.generateKeys();
        if (this.currencyData) {
          if (!this.currencyData.flag) {
            console.warn(
              "[NumericKeyboard] currencyData.flag missing",
              this.currencyData
            );
          }
          if (typeof this.currencyData.input_buffer === "undefined") {
            console.warn(
              "[NumericKeyboard] currencyData.input_buffer missing",
              this.currencyData
            );
          }
        }
        return result;
      } catch (error) {
        console.error("Error in keys computed property:", error);
        return [];
      }
    },

    // Check if current input reaches or exceeds total price
    isPaymentComplete() {
      try {
        const currentValue = parseFloat(this.value) || 0;
        return currentValue >= this.totalPrice;
      } catch (error) {
        return false;
      }
    },

    // Calculate change amount
    changeAmount() {
      try {
        const currentValue = parseFloat(this.value) || 0;
        return Math.max(0, currentValue - this.totalPrice);
      } catch (error) {
        return 0;
      }
    },
  },
  methods: {
    dlog(label, payload) {
      try {
        if (this.debug) console.debug(`[NumericKeyboard] ${label}`, payload);
      } catch (_) {}
    },
    generateKeys() {
      const baseKeys = [
        [
          { id: 1, label: "7" },
          { id: 2, label: "8" },
          { id: 3, label: "9" },
          { id: 4, label: "100,000", class: "primary--text" },
        ],
        [
          { id: 5, label: "4" },
          { id: 6, label: "5" },
          { id: 7, label: "6" },
          { id: 8, label: "50,000", class: "primary--text" },
        ],
        [
          { id: 9, label: "1" },
          { id: 10, label: "2" },
          { id: 11, label: "3" },
          {
            id: 12,
            label: "20,000",
            class: "primary--text",
          },
        ],
        [
          { id: 17, label: "." },
          { id: 18, label: "0" },
          { id: 19, icon: "fal fa-backspace", action: this.backspace },
          {
            id: 20,
            label: this.$t ? this.$t("full") : "Full",
            class: "primary--text",
            action: this.full,
          },
        ],
        [
          {
            id: 21,
            icon: "fal fa-user-circle",
            action: () => this.$emit("customer"),
          },
          {
            id: 22,
            icon: "fal fa-edit",
            action: () => this.$emit("openNoted"),
          },
          {
            id: 23,
            icon: "/images/ic_currency.svg",
            action: () => this.$emit("exchangeRate"),
          },
          {
            id: 24,
            label: this.$t ? this.$t("ok") : "OK",
            action: () => this.$emit("submit"),
            class: "submit-btn",
            cols: this.showNote ? 3 : 6,
          },
        ],
      ];

      if (!this.showNote) {
        baseKeys[4] = baseKeys[4].filter((key) => key.id !== 22);
      }

      // Ensure all keys have valid id properties
      const finalResult = baseKeys.map((row) =>
        row.map((key) => ({
          ...key,
          id: key.id || Math.random().toString(36).substr(2, 9),
        }))
      );

      return finalResult;
    },

    appendToInput(value) {
      try {
        if (!value || typeof value !== "string") return;

        // if value have , remove it
        if (value.includes(",")) {
          //  convert value to number
          const valInt = parseFloat(value.replace(/,/g, "")) || 0;
          // convert this.value to number
          const thisValueInt = parseFloat(this.value.replace(/,/g, "")) || 0;
          // total with convert to string
          const total = (thisValueInt + valInt).toString();
          // if this.value is 0, replace it with valInt
          this.$emit("input", total);
          return;
        }
        // value = value.replace(/,/g, "");
        this.$emit("input", this.value + value);
      } catch (error) {
        console.error("Error appending to input:", error);
      }
    },
    backspace() {
      try {
        // length the value if value is "" then set to 0
        if (!this.value || this.value.length === 0 || this.value.length <= 1) {
          this.$emit("input", "0");
          return;
        } else {
          // remove last character
          if (this.value.includes(".")) {
            // 119191.04999999999 convert to float : 119191.05
            const value = parseFloat(this.value).toFixed(2);
            // convert to string
            const val = value.toString();
            // if value has a dot, remove it
            const removeDot = val.replace(".", "");
            // remove last character
            this.$emit("input", removeDot.slice(0, -1));
          } else {
            this.$emit("input", this.value.slice(0, -1));
          }
        }
      } catch (error) {
        console.error("Error in backspace:", error);
        this.$emit("input", "0");
      }
    },
    clear() {
      try {
        this.$emit("input", "0");
      } catch (error) {
        console.error("Error in clear method:", error);
      }
    },
    full() {
      try {
        const total = this.totalPrice || 0;
        this.$emit("input", total.toString());
      } catch (error) {
        console.error("Error in full method:", error);
        this.$emit("input", "0");
      }
    },
    handleKeydown(event) {
      // Skip if user is typing in another input field
      const activeEl = document.activeElement;
      const tag = activeEl?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        activeEl?.isContentEditable
      ) {
        return;
      }

      if (this.open_noted || !event || !event.key) return;
      // if backspace key is pressed
      switch (event.key) {
        case "1":
          event.preventDefault();
          this.appendToInput("1");
          break;
        case "2":
          event.preventDefault();
          this.appendToInput("2");
          break;
        case "3":
          event.preventDefault();
          this.appendToInput("3");
          break;
        case "4":
          event.preventDefault();
          this.appendToInput("4");
          break;
        case "5":
          event.preventDefault();
          this.appendToInput("5");
          break;
        case "6":
          event.preventDefault();
          this.appendToInput("6");
          break;
        case "7":
          event.preventDefault();
          this.appendToInput("7");
          break;
        case "8":
          event.preventDefault();
          this.appendToInput("8");
          break;
        case "9":
          event.preventDefault();
          this.appendToInput("9");
          break;
        case "0":
          event.preventDefault();
          this.appendToInput("0");
          break;
        case ".":
          event.preventDefault();
          this.appendToInput(".");
          break;
        case "Enter":
          event.preventDefault();
          this.$emit("submit");
          break;
        case "Backspace":
          event.preventDefault();
          this.backspace();
          break;
      }
    },
  },
};
</script>

<style scoped>
/* PosSmart brutal keys — same look as the customer phone-numpad buttons
   (white, 1px slate border, rounded-xl, weight 900, active brand). */
.numeric-keyboard {
  padding: 2px;
}

.numeric-keyboard .v-btn {
  height: 60px;
  width: calc(100% - 8px);
  margin: 4px;
  border-radius: 12px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: #141021 !important;
  font-weight: 900 !important;
  font-size: 20px !important;
  box-shadow: none !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  transition: background-color 0.12s ease, color 0.12s ease,
    border-color 0.12s ease;
}

.numeric-keyboard .v-btn:hover {
  background: #f8fafc !important;
}

.numeric-keyboard .v-btn:active {
  background: #5b39c9 !important;
  color: #ffffff !important;
  border-color: #5b39c9 !important;
}

/* quick-amount keys (100,000 / 50,000 / 20,000 / Full) keep brand text */
.numeric-keyboard .v-btn.primary--text {
  color: #5b39c9 !important;
  font-size: 14px !important;
}
.numeric-keyboard .v-btn.primary--text:active {
  color: #ffffff !important;
}

/* icon keys (customer / note / exchange-rate / backspace) */
.numeric-keyboard .v-btn .v-icon {
  color: #64748b;
}
.numeric-keyboard .v-btn:active .v-icon {
  color: #ffffff;
}

/* submit (OK) key — brutal brand */
.numeric-keyboard .row .col button.submit-btn {
  background-color: #5b39c9 !important;
  color: #fff !important;
  border: none !important;
  box-shadow: none !important;
}
.numeric-keyboard .row .col button.submit-btn:active {
  transform: translate(1px, 1px);
  box-shadow: none !important;
}
</style>
